import { australianState } from "@fla/common";
import { RadioSelect, RelationshipIcon, Select, TextInput } from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { GetStarted_RelationshipSchema } from "definitions";
import { GetStartedStoreTypes } from "interfaces";
import { selectUserProfile } from "pages/User";
import { FC, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

interface IProps {
  formId: string;
  store: GetStartedStoreTypes;
  onSubmit: SubmitHandler<GetStartedStoreTypes>;
}

const RelationshipDetails: FC<IProps> = ({ formId, store, onSubmit }) => {
  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: store,
    resolver: yupResolver(GetStarted_RelationshipSchema)
  });

  const user = useSelector(selectUserProfile);

  useEffect(() => {
    // Update form fields when store data changes
    if (!user) {
      setValue("relationship.first_name", store.relationship.first_name);
      setValue("relationship.state", store.relationship.state);
      setValue("relationship.divided_assets", store.relationship.divided_assets);
      setValue("relationship.reached_agreement", store.relationship.reached_agreement);
      setValue("relationship.relationship", store.relationship.relationship);
      setValue("relationship.defacto_live_2y", store.relationship.defacto_live_2y);
      setValue("relationship.separated_2y", store.relationship.separated_2y);
      setValue("relationship.divorced", store.relationship.divorced);
      setValue("relationship.divorced_1y", store.relationship.divorced_1y);
      setValue("relationship.bfa_exists", store.relationship.bfa_exists);
    }
  }, [user]);
  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b space-y-2">
        <RelationshipIcon classes="w-14 h-14 mx-auto" />
        <h3 className="text-center">Relationship Details</h3>
      </div>

      <section className="space-y-5">
        <TextInput
          title="What is your first name?"
          control={control}
          name="relationship.first_name"
          id="relationship.first_name"
        />

        <Select
          title="What state do you live in?"
          control={control}
          name="relationship.state"
          id="relationship.state"
          placeholder="Select State"
          options={australianState}
        />

        <RadioSelect
          title="Do you need Consent Orders to divide your assets & debts?"
          control={control}
          name="relationship.divided_assets"
          id="relationship.divided_assets"
          options={["yes", "no"]}
        />

        <RadioSelect
          title="Have you both reached an agreement about how to divide your assets & debts?"
          control={control}
          name="relationship.reached_agreement"
          id="relationship.reached_agreement"
          options={["yes", "no"]}
        />

        <RadioSelect
          title="Were you married or in a de facto relationship?"
          control={control}
          name="relationship.relationship"
          id="relationship.relationship"
          options={["married", "de-facto"]}
          onChange={val => {
            if (val === "de-facto") {
              setValue("relationship.divorced", "");
              setValue("relationship.divorced_1y", "");
            } else {
              setValue("relationship.defacto_live_2y", "");
              setValue("relationship.separated_2y", "");
            }
          }}
        />

        {watch("relationship.relationship") === "de-facto" ? (
          <RadioSelect
            title="Did you both live together for at least 2 years?"
            control={control}
            name="relationship.defacto_live_2y"
            id="relationship.defacto_live_2y"
            options={["yes", "no"]}
          />
        ) : null}

        {watch("relationship.relationship") === "de-facto" ? (
          <RadioSelect
            title="Have you been separated for more than 2 years?"
            control={control}
            name="relationship.separated_2y"
            id="relationship.separated_2y"
            options={["yes", "no"]}
          />
        ) : null}

        {watch("relationship.relationship") === "married" ? (
          <RadioSelect
            title="Are you legally divorced?"
            control={control}
            name="relationship.divorced"
            id="relationship.divorced"
            options={["yes", "no"]}
          />
        ) : null}

        {watch("relationship.relationship") === "married" && watch("relationship.divorced") === "yes" ? (
          <RadioSelect
            title="Have you been divorced for more than 1 year?"
            control={control}
            name="relationship.divorced_1y"
            id="relationship.divorced_1y"
            options={["yes", "no"]}
          />
        ) : null}

        <RadioSelect
          title="Have you signed a binding financial agreement (BFA) that's still current?"
          subTitle="You need to sign a special document to terminate a BFA"
          control={control}
          name="relationship.bfa_exists"
          id="relationship.bfa_exists"
          options={["yes", "no"]}
        />
      </section>
    </form>
  );
};

export default RelationshipDetails;
